import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  CircularProgress,
} from '@mui/material'
import {
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Trash2,
  Calendar,
  MapPin,
  Users,
} from 'lucide-react'
import API from '../../utils/API'
import { useAuth } from '../../utils/authContext'

const EventStatusBadge = ({ event }) => {
  // Determine status based on API data
  let status = 'draft'
  if (event.is_finished) {
    status = 'completed'
  } else if (event.published) {
    status = 'published'
  } else {
    status = 'draft'
  }
  
  const statusConfig = {
    draft: { color: 'default', label: 'Draft' },
    published: { color: 'success', label: 'Published' },
    active: { color: 'primary', label: 'Active' },
    completed: { color: 'secondary', label: 'Completed' },
    cancelled: { color: 'error', label: 'Cancelled' },
  }
  
  const config = statusConfig[status] || statusConfig.draft
  
  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      variant="outlined"
    />
  )
}

const EventRow = ({ event, onView, onEdit, onDelete }) => {
      const formatDate = (dateString) => {
        if (!dateString) return 'TBD'
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', { 
          year: 'numeric', 
          month: 'short', 
          day: 'numeric' 
        })
      }
      
      return (
        <TableRow 
          sx={{ 
            '&:hover': { backgroundColor: 'grey.50' },
            '&:last-child td, &:last-child th': { border: 0 }
          }}
        >
          <TableCell>
            <Box>
              <Typography variant="body2" fontWeight="semibold">{event.production?.name || 'Untitled Event'}</Typography>
              <Typography variant="caption" color="grey.600">
                {event.venue || 'No venue'}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Calendar size={16} color="gray" />
              <Typography variant="body2">{formatDate(event.date)}</Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MapPin size={16} color="gray" />
              <Typography variant="body2">{event.location || 'No location'}</Typography>
            </Box>
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Users size={16} color="gray" />
              <Typography variant="body2">{event.capacity || 'N/A'}</Typography>
            </Box>
          </TableCell>
          <TableCell>
            <EventStatusBadge event={event} />
          </TableCell>
          <TableCell>
            <Typography variant="body2" color="grey.600">
              ${event.ticket_price || '0'}
            </Typography>
          </TableCell>
          <TableCell>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="View Event">
                <IconButton
                  size="small"
                  onClick={() => onView(event)}
                  aria-label="View event"
                >
                  <Eye size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Edit Event">
                <IconButton
                  size="small"
                  onClick={() => onEdit(event)}
                  aria-label="Edit event"
                >
                  <Edit size={16} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete Event">
                <IconButton
                  size="small"
                  onClick={() => onDelete(event)}
                  aria-label="Delete event"
                  color="error"
                >
                  <Trash2 size={16} />
                </IconButton>
              </Tooltip>
            </Box>
          </TableCell>
        </TableRow>
      )
    }

export default function Events() {
      const { showrunner, loading: authLoading } = useAuth()
      const navigate = useNavigate()
      const [searchTerm, setSearchTerm] = useState('')
      const [statusFilter, setStatusFilter] = useState('all')
      const [loading, setLoading] = useState(true)
      const [events, setEvents] = useState([])
      
      useEffect(() => {
        if (authLoading) return
        if (!showrunner?.id) {
          setLoading(false)
          return
        }
        fetchEvents()
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [showrunner, authLoading])
      
      const fetchEvents = async () => {
        if (!showrunner?.id) {
          setLoading(false)
          return
        }
        
        try {
          setLoading(true)
          
          const showrunnerId = showrunner.id
          
          // Fetch shows/events
          const eventsResponse = await API.getShows(`?showrunner_id=${showrunnerId}`)
          setEvents(eventsResponse.data)
        } catch (error) {
          console.error('Error fetching events:', error)
        } finally {
          setLoading(false)
        }
      }
  
      const filteredEvents = events.filter(event => {
        const matchesSearch = event.production?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             event.venue?.toLowerCase().includes(searchTerm.toLowerCase())
        
        // Determine status for filtering
        let eventStatus = 'draft'
        if (event.is_finished) {
          eventStatus = 'completed'
        } else if (event.published) {
          eventStatus = 'published'
        }
        
        const matchesStatus = statusFilter === 'all' || eventStatus === statusFilter
        
        return matchesSearch && matchesStatus
      })
      
      const handleCreateEvent = () => {
        navigate('/showrunner/events/create')
      }
      
      const handleViewEvent = (event) => {
        navigate(`/showrunner/show?showID=${event.id}`)
      }
      
      const handleEditEvent = (event) => {
        navigate(`/showrunner/show?showID=${event.id}`)
      }
      
      const handleDeleteEvent = async (event) => {
        if (window.confirm(`Are you sure you want to delete "${event.production?.name || 'Untitled Event'}"?`)) {
          try {
            await API.deleteShow(event.id)
            await fetchEvents() // Refresh the list
          } catch (error) {
            console.error('Error deleting event:', error)
          }
        }
      }
      
      if (loading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress size={60} />
          </Box>
        )
      }
  
  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Events</Typography>
          <Typography color="grey.600">
            Manage your shows, performances, and events
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateEvent}
          startIcon={<Plus size={16} />}
        >
          Create Event
        </Button>
      </Box>
      
      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              placeholder="Search events..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              sx={{ maxWidth: '300px' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={16} color="gray" />
                  </InputAdornment>
                ),
              }}
            />
            
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
            
            <Button
              variant="outlined"
              size="small"
              startIcon={<Filter size={16} />}
            >
              More Filters
            </Button>
          </Box>
        </CardContent>
      </Card>
      
      {/* Events Table */}
      <Card>
        <CardContent sx={{ padding: 0 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Event</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Capacity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ticket Price</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEvents.map((event) => (
                <EventRow
                  key={event.id}
                  event={event}
                  onView={handleViewEvent}
                  onEdit={handleEditEvent}
                  onDelete={handleDeleteEvent}
                />
              ))}
            </TableBody>
          </Table>
          
          {filteredEvents.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography color="grey.500">No events found</Typography>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  )
}
